import { FC, Ref } from 'react'
import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { UserWithImages, FeedMedia } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'

import { makeRoundupUrl } from '../../../utils/routes'
import { MediaState } from './Media'
import { ProductsButton } from '../ProductsButton'
import { ShareButton } from '../ShareButton'
import { MuteButton } from '../video/MuteButton'
import { FEED_MEDIA_CONTAINER_Z_INDEX } from '../../../utils/zIndex'
import { Views } from './Views'

export const FEED_RIGHT_BAR_BOTTOM_OFFSET = 36
export const FEED_RIGHT_BAR_RIGHT_OFFSET = 24

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'black',
    overflow: 'hidden',
  },
  rightBar: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    position: 'absolute',
    right: FEED_RIGHT_BAR_RIGHT_OFFSET,
    top: 32,
    bottom: FEED_RIGHT_BAR_BOTTOM_OFFSET,
    pointerEvents: 'none',
  },
  rightBarTop: {
    paddingLeft: 10,
  },
  rightBarInner: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'auto',
    zIndex: FEED_MEDIA_CONTAINER_Z_INDEX,
  },
  muteButton: {
    marginBottom: 6,
  },
  views: {
    marginBottom: 8,
  },
  shareButton: {
    marginBottom: 12,
  },
})

interface Props {
  isVisible: boolean
  creator: UserWithImages
  media: FeedMedia
  state: MediaState
  onToggleProducts: () => void
  productsActive: boolean
  className?: string
  innerRef?: Ref<HTMLDivElement>
}

export const Container: FC<Props> = flowMax(
  addDisplayName('Container'),
  addProps(({ media: { content } }) => ({
    isVideo: content.type === 'video',
  })),
  addNavigate,
  addHandlers({
    navigateToRoundup:
      ({ navigate }) =>
      () => {
        navigate(makeRoundupUrl())
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    isVisible,
    creator,
    media,
    media: { id },
    isVideo,
    onToggleProducts,
    productsActive,
    innerRef,
    classes,
    className,
    children,
  }) => (
    <div
      id={id.toString()}
      ref={innerRef}
      className={cx(classes.container, className)}
    >
      {isVisible && (
        <>
          {children}
          <div className={classes.rightBar}>
            <div
              className={cx(classes.rightBarTop, classes.rightBarInner)}
            ></div>
            <div className={classes.rightBarInner}>
              {isVideo && <MuteButton className={classes.muteButton} />}
              {media.numViews > 0 && (
                <Views views={media.numViews} className={classes.views} />
              )}
              <ShareButton
                context={{
                  mediaId: media.id,
                  creatorId: creator.id,
                }}
                className={classes.shareButton}
              />
              <ProductsButton
                media={media}
                onClick={onToggleProducts}
                active={productsActive}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
)
